import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlayIcon from '../assets/landing/play-icon.svg?react'
import { landingAssetsForLocale } from '../config/landingAssets'

interface EaseOfUseSectionProps {
  headingKey: 'easeOfUse.title' | 'easeOfUse.videoHeading'
  showDescription: boolean
  videoId: string
}

export default function EaseOfUseSection({
  headingKey,
  showDescription,
  videoId,
}: EaseOfUseSectionProps) {
  const { t, i18n } = useTranslation('landingPage')
  const assets = landingAssetsForLocale(i18n.language)
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&controls=0&fs=0`
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  const handlePlayClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      setIsVideoPlaying(true)
    },
    [setIsVideoPlaying],
  )

  return (
    <section className='space-y-6 bg-[#FEFBE9] px-4 py-10'>
      <div className='space-y-2 text-center'>
        <h2 className='text-gray-12 mb-6 whitespace-pre-line text-2xl font-semibold'>
          {t(headingKey)}
        </h2>
        {showDescription && (
          <p className='text-gray-12 tracking-0 whitespace-pre-line text-left text-base font-normal leading-[160%]'>
            {t('easeOfUse.description')}
          </p>
        )}
      </div>

      <div className='relative mx-auto max-w-md overflow-hidden rounded-lg'>
        {!isVideoPlaying ? (
          <>
            <img src={assets.videoThumbnail} alt='' className='w-full rounded-lg' loading='lazy' />
            <button
              onClick={handlePlayClick}
              className='absolute inset-0 flex items-center justify-center rounded-lg transition-colors hover:bg-black/10'
              aria-label='Play video'
            >
              <PlayIcon className='h-14 w-14' />
            </button>
          </>
        ) : (
          <div className='relative w-full' style={{ paddingBottom: '56.25%' }}>
            <iframe
              className='absolute left-0 top-0 h-full w-full rounded-lg'
              src={embedUrl}
              title={t('easeOfUse.videoTitle')}
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen={true}
            />
          </div>
        )}
      </div>
    </section>
  )
}
